
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Plus, 
  Users, 
  LayoutDashboard, 
  LogOut, 
  ShieldCheck, 
  Database, 
  Menu, 
  Lock, 
  Compass, 
  Loader2, 
  RefreshCw, 
  Search, 
  Bell, 
  Info, 
  ChevronRight, 
  TrendingUp, 
  CreditCard, 
  UserCheck, 
  Baby, 
  PieChart as PieIcon, 
  Filter, 
  ArrowUpRight, 
  Activity,
  BarChart3,
  Wallet,
  Landmark,
  LayoutGrid,
  List,
  Target,
  Mail,
  Send,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Tooltip, 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { Person, Relationship, Asset, AssetCategory, UserProfile, NomineeDiscoveryItem, LegalPageType } from './types';
import { NomineeCard } from './components/NomineeCard';
import { AssetCard } from './components/AssetCard';
import { AddNomineeModal } from './components/AddNomineeModal';
import { AddAssetModal } from './components/AddAssetModal';
import { nomineeService } from './services/nomineeService';
import { LoginPage } from './components/LoginPage';
import { LandingPage } from './components/LandingPage';
import { InfoPage } from './components/InfoPages';
import { UserProfileModal } from './components/UserProfileModal';
import { NomineePortal } from './components/NomineePortal';
import { authService } from './services/authService';
import { dbService } from './services/dbService';
import { emailService } from './services/emailService';

const COLORS = ['#4318FF', '#6AD2FF', '#FFB547', '#01B574', '#707EAE', '#E31B23', '#7551FF'];

const App: React.FC = () => {
  const [currentUser, setCurrentUser] = useState<UserProfile | null>(null);
  const [currentRoute, setCurrentRoute] = useState<'landing' | 'login' | 'signup' | 'app'>('landing');
  const [currentView, setCurrentView] = useState<'dashboard' | 'beneficiaries' | 'nominee-portal' | 'analytics'>('dashboard');
  
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isAddNomineeModalOpen, setIsAddNomineeModalOpen] = useState(false);
  const [editingNominee, setEditingNominee] = useState<Person | null>(null);
  const [isAddAssetModalOpen, setIsAddAssetModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<Asset | null>(null);
  const [activeInfoModal, setActiveInfoModal] = useState<LegalPageType | null>(null);
  
  const [analyticsViewMode, setAnalyticsViewMode] = useState<'composition' | 'distribution'>('composition');
  const [chartType, setChartType] = useState<'pie' | 'bar' | 'list'>('pie');
  
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'info' | 'error'} | null>(null);

  const [people, setPeople] = useState<Person[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [inheritances, setInheritances] = useState<NomineeDiscoveryItem[]>([]);

  // --- Browser History (Mobile Back Button) Fixed Logic ---
  
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      const state = event.state;
      if (state) {
        if (state.route) setCurrentRoute(state.route);
        if (state.view) setCurrentView(state.view);
      } else {
        setCurrentRoute('landing');
        setCurrentView('dashboard');
      }
      setIsMobileMenuOpen(false);
      setActiveInfoModal(null);
    };

    window.addEventListener('popstate', handlePopState);
    
    if (!window.history.state) {
      window.history.replaceState({ route: 'landing', view: 'dashboard' }, '', '');
      window.history.pushState({ route: 'landing', view: 'dashboard' }, '', '');
    }

    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigateToRoute = (route: 'landing' | 'login' | 'signup' | 'app') => {
    if (route !== currentRoute) {
      setCurrentRoute(route);
      window.history.pushState({ route, view: currentView }, '', '');
    }
  };

  const navigateToView = (view: 'dashboard' | 'beneficiaries' | 'nominee-portal' | 'analytics') => {
    if (view !== currentView) {
      setCurrentView(view);
      window.history.pushState({ route: currentRoute, view }, '', '');
    }
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const sessionUser = authService.getCurrentSession();
    if (sessionUser) {
      setCurrentUser(sessionUser);
      setCurrentRoute('app');
      const startView = sessionUser.role === 'nominee' ? 'nominee-portal' : 'dashboard';
      setCurrentView(startView);
      window.history.replaceState({ route: 'app', view: startView }, '', '');
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (!currentUser?.id) return;
      setIsLoading(true);
      try {
        if (currentUser.role === 'owner') {
          const [fetchedPeople, fetchedAssets] = await Promise.all([
            dbService.getNominees(currentUser.id),
            dbService.getAssets(currentUser.id)
          ]);
          setPeople(fetchedPeople || []);
          setAssets(fetchedAssets || []);
        } else {
          const discovered = await nomineeService.discoverInheritances(currentUser.email);
          setInheritances(discovered || []);
        }
      } catch (e) {
        console.error("Data fetch error:", e);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [currentUser]);

  const handleSaveNominee = async (person: Person) => {
    if (!currentUser?.id) return;
    setIsSyncing(true);
    try {
      const isNewNominee = person.id.startsWith('p-');

      if (!isNewNominee) {
        const success = await dbService.updateNominee(currentUser.id, person);
        if (success) {
          setPeople(prev => prev.map(p => p.id === person.id ? person : p));
        }
      } else {
        const newPerson = await dbService.addNominee(currentUser.id, person);
        if (newPerson) {
          setPeople(prev => [...prev, newPerson]);
          
          // SEND REAL EMAIL via EmailJS
          showNotification(`Transmitting invite to ${person.email}...`, 'info');
          const emailResult = await emailService.sendNomineeInvite(person.email, person.fullName, currentUser.name);
          
          if (emailResult.success) {
            showNotification(`Delivered to ${person.email}`, 'success');
          } else {
            showNotification(emailResult.error || "Email delivery failed", 'error');
          }
        }
      }
      setEditingNominee(null);
      setIsAddNomineeModalOpen(false);
    } catch (err) {
      console.error("Save nominee failed:", err);
      showNotification("Critical save error", "error");
    } finally {
      setIsSyncing(false);
    }
  };

  const showNotification = (message: string, type: 'success' | 'info' | 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleDeleteNominee = async (id: string) => {
    const target = people.find(p => p.id === id);
    if (!target || !currentUser?.id) return;
    if (window.confirm(`Are you sure you want to remove ${target.fullName}?`)) {
      setIsSyncing(true);
      try {
        const success = await dbService.deleteNominee(currentUser.id, id);
        if (success) {
          setPeople(prev => prev.filter(p => p.id !== id));
          const updatedAssets = assets.map(asset => ({
            ...asset,
            allocations: asset.allocations.filter(al => al.personId !== id)
          }));
          setAssets(updatedAssets);
          for (const asset of updatedAssets) {
              await dbService.updateAsset(currentUser.id!, asset);
          }
          if (target.email) await dbService.removeNomineeFromDiscovery(currentUser.id, target.email);
        }
      } finally {
        setIsSyncing(false);
      }
    }
  };

  const handleSaveAsset = async (asset: Asset) => {
    if (!currentUser?.id) return;
    setIsSyncing(true);
    try {
      const isNewAsset = asset.id.startsWith('a-');
      if (isNewAsset) {
          const savedAsset = await dbService.addAsset(currentUser.id, asset);
          if (savedAsset) setAssets(prev => [savedAsset, ...prev]);
      } else {
          const success = await dbService.updateAsset(currentUser.id, asset);
          if (success) setAssets(prev => prev.map(a => a.id === asset.id ? asset : a));
      }
      setEditingAsset(null);
      setIsAddAssetModalOpen(false);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDeleteAsset = async (id: string) => {
    if (!currentUser?.id) return;
    if (window.confirm("Remove this asset from your legacy vault?")) {
        setIsSyncing(true);
        try {
          const success = await dbService.deleteAsset(currentUser.id, id);
          if (success) setAssets(prev => prev.filter(a => a.id !== id));
        } finally {
          setIsSyncing(false);
        }
    }
  };

  const handleUpdateAssetAllocation = async (updatedAsset: Asset) => {
    if (!currentUser?.id) return;
    setAssets(prev => prev.map(a => a.id === updatedAsset.id ? updatedAsset : a));
    await dbService.updateAsset(currentUser.id, updatedAsset);
  };

  const handleProfileSave = async (updatedUser: UserProfile) => {
    setIsSyncing(true);
    try {
      const success = await authService.updateProfile(updatedUser);
      if (success) {
        setCurrentUser(updatedUser);
        showNotification("Profile updated successfully", 'success');
      } else {
        showNotification("Failed to update profile", 'info');
      }
    } catch (err) {
      console.error("Profile save failed:", err);
    } finally {
      setIsSyncing(false);
    }
  };

  const totalEstateValue = useMemo(() => assets.reduce((sum, a) => sum + a.value, 0), [assets]);
  const estateHealth = useMemo(() => {
    if (assets.length === 0) return 0;
    const fullyAllocatedCount = assets.filter(a => a.allocations.reduce((s, al) => s + al.percentage, 0) === 100).length;
    return Math.round((fullyAllocatedCount / assets.length) * 100);
  }, [assets]);

  const globalStats = useMemo(() => {
    return people.map(person => {
      let personTotalValue = 0;
      assets.forEach(asset => {
        const allocation = asset.allocations.find(a => a.personId === person.id);
        if (allocation) personTotalValue += (asset.value * allocation.percentage / 100);
      });
      const sharePercentage = totalEstateValue > 0 ? (personTotalValue / totalEstateValue) * 100 : 0;
      const assetCount = assets.filter(a => a.allocations.some(al => al.personId === person.id)).length;
      return { ...person, value: personTotalValue, sharePercentage: Number(sharePercentage.toFixed(2)), assetCount };
    });
  }, [people, assets, totalEstateValue]);

  const analyticsData = useMemo(() => {
    const categoryMap: Record<string, number> = {};
    const institutionMap: Record<string, number> = {};
    const nomineeValueMap: Record<string, number> = {};

    assets.forEach(asset => {
      categoryMap[asset.category] = (categoryMap[asset.category] || 0) + asset.value;
      if (asset.institution) {
        institutionMap[asset.institution] = (institutionMap[asset.institution] || 0) + asset.value;
      }
      asset.allocations.forEach(al => {
        const person = people.find(p => p.id === al.personId);
        if (person) {
          nomineeValueMap[person.fullName] = (nomineeValueMap[person.fullName] || 0) + (asset.value * al.percentage / 100);
        }
      });
    });

    const categoryData = Object.entries(categoryMap).map(([name, value]) => ({ name, value }));
    const institutionData = Object.entries(institutionMap)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
    const nomineeData = Object.entries(nomineeValueMap).map(([name, value]) => ({ name, value }));

    return { categoryData, institutionData, nomineeData };
  }, [assets, people]);

  const filteredNominees = useMemo(() => {
    if (!searchQuery) return people;
    const query = searchQuery.toLowerCase();
    return people.filter(p => 
      p.fullName.toLowerCase().includes(query) ||
      p.relationship.toLowerCase().includes(query) ||
      p.email.toLowerCase().includes(query)
    );
  }, [people, searchQuery]);

  const filteredAssets = useMemo(() => {
    if (!searchQuery) return assets;
    const query = searchQuery.toLowerCase();
    return assets.filter(a => 
      a.name.toLowerCase().includes(query) ||
      a.category.toLowerCase().includes(query) ||
      (a.institution && a.institution.toLowerCase().includes(query))
    );
  }, [assets, searchQuery]);

  const handleLogout = useCallback(() => {
    authService.logout();
    setCurrentUser(null);
    setCurrentRoute('landing');
    setCurrentView('dashboard');
    setIsMobileMenuOpen(false);
    window.history.replaceState({ route: 'landing', view: 'dashboard' }, '', '');
    window.history.pushState({ route: 'landing', view: 'dashboard' }, '', '');
  }, []);

  const handleLoginSuccess = useCallback((user: UserProfile) => {
    setCurrentUser(user);
    setCurrentRoute('app');
    const startView = user.role === 'nominee' ? 'nominee-portal' : 'dashboard';
    setCurrentView(startView);
    window.history.replaceState({ route: 'app', view: startView }, '', '');
    window.history.pushState({ route: 'app', view: startView }, '', '');
  }, []);

  const isOwner = currentUser?.role === 'owner';

  return (
    <>
      {/* Toast Notification */}
      {notification && (
        <div className="fixed top-6 right-6 z-[100] animate-slide-up">
           <div className={`px-6 py-4 rounded-[20px] shadow-2xl flex items-center gap-4 border border-white/20 backdrop-blur-xl ${
             notification.type === 'success' ? 'bg-emerald-600 text-white' : 
             notification.type === 'error' ? 'bg-rose-600 text-white' :
             'bg-indigo-600 text-white'
           }`}>
              {notification.type === 'success' ? <CheckCircle2 size={20} /> : 
               notification.type === 'error' ? <AlertCircle size={20} /> :
               <Mail size={20} className="animate-pulse" />}
              <span className="text-sm font-bold uppercase tracking-wider">{notification.message}</span>
           </div>
        </div>
      )}

      {!currentUser ? (
        currentRoute === 'login' || currentRoute === 'signup' ? (
          <LoginPage onLogin={handleLoginSuccess} initialMode={currentRoute === 'signup' ? 'signup' : 'login'} onBackToHome={() => navigateToRoute('landing')} />
        ) : (
          <LandingPage onStart={(mode) => navigateToRoute(mode === 'login' ? 'login' : 'signup')} onNavigate={(t) => setActiveInfoModal(t)} />
        )
      ) : (
        <div className="flex bg-[#F4F7FE] min-h-screen text-slate-800 font-sans">
          <aside className={`fixed h-full z-50 w-72 bg-white transition-transform duration-300 transform rounded-r-[30px] shadow-[0px_4px_45px_0px_rgba(0,0,0,0.05)] ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
            <div className="flex flex-col h-full">
              <div className="p-8 pt-10 border-b border-gray-100 flex items-center justify-center">
                <h1 className="text-2xl font-black tracking-tighter text-[#2B3674] uppercase">
                  Nominee<span className="text-indigo-600 font-bold">Desk</span>
                </h1>
              </div>
              
              <nav className="flex-1 mt-8 px-6 space-y-2">
                {isOwner ? (
                  <>
                    <button 
                      onClick={() => navigateToView('dashboard')} 
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl font-bold transition-all relative group ${currentView === 'dashboard' ? 'text-[#2B3674]' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <LayoutDashboard size={20} className={currentView === 'dashboard' ? 'text-indigo-600' : ''} />
                      <span className="text-sm">Main Dashboard</span>
                      {currentView === 'dashboard' && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-indigo-600 rounded-l-full" />}
                    </button>
                    <button 
                      onClick={() => navigateToView('beneficiaries')} 
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl font-bold transition-all relative group ${currentView === 'beneficiaries' ? 'text-[#2B3674]' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <Users size={20} className={currentView === 'beneficiaries' ? 'text-indigo-600' : ''} />
                      <span className="text-sm">Legacy Directory</span>
                      {currentView === 'beneficiaries' && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-indigo-600 rounded-l-full" />}
                    </button>
                    <button 
                      onClick={() => navigateToView('analytics')} 
                      className={`w-full flex items-center gap-4 px-4 py-4 rounded-xl font-bold transition-all relative group ${currentView === 'analytics' ? 'text-[#2B3674]' : 'text-gray-400 hover:text-gray-600'}`}
                    >
                      <BarChart3 size={20} className={currentView === 'analytics' ? 'text-indigo-600' : ''} />
                      <span className="text-sm">Insight Analytics</span>
                      {currentView === 'analytics' && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-indigo-600 rounded-l-full" />}
                    </button>
                  </>
                ) : (
                  <button 
                    onClick={() => navigateToView('nominee-portal')} 
                    className="w-full flex items-center gap-4 px-4 py-4 rounded-xl font-bold text-[#2B3674] relative"
                  >
                    <Compass size={20} className="text-indigo-600" />
                    <span className="text-sm">Heritage Access</span>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-indigo-600 rounded-l-full" />
                  </button>
                )}
              </nav>

              <div className="p-8 space-y-4">
                 <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-[20px] p-5 text-white shadow-xl relative overflow-hidden group">
                    <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                    <div className="relative z-10">
                       <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                          <ShieldCheck size={20} />
                       </div>
                       <p className="text-xs font-bold uppercase tracking-widest opacity-80">Security Protocol</p>
                       <p className="text-xs font-medium mt-1 leading-relaxed">End-to-End Encryption active.</p>
                    </div>
                 </div>
                 <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl font-bold text-gray-400 hover:text-rose-500 hover:bg-rose-50 transition-all">
                   <LogOut size={20} />
                   <span className="text-sm">Logout</span>
                 </button>
              </div>
            </div>
          </aside>

          <main className="flex-1 lg:ml-72 min-h-screen p-4 md:p-8 pt-4 overflow-x-hidden">
            <header className="sticky top-4 z-40 mb-8 bg-white/40 backdrop-blur-xl border border-white/20 rounded-[20px] px-6 py-4 flex items-center justify-between shadow-[0px_4px_45px_0px_rgba(112,144,176,0.12)] max-w-[1600px] mx-auto">
              <div>
                <p className="text-[10px] font-medium text-[#707EAE] mb-1">
                  Pages / {
                    currentView === 'nominee-portal' ? 'Heritage' : 
                    currentView === 'beneficiaries' ? 'Legacy Directory' : 
                    currentView === 'analytics' ? 'Analytics' :
                    'Main Dashboard'
                  }
                </p>
                <h2 className="text-2xl font-bold text-[#2B3674] capitalize tracking-tight">
                  {
                    currentView === 'nominee-portal' ? 'Heritage Portal' : 
                    currentView === 'beneficiaries' ? 'Legacy Directory' : 
                    currentView === 'analytics' ? 'Insight Analytics' :
                    'Main Dashboard'
                  }
                </h2>
              </div>

              <div className="flex items-center gap-4 bg-white p-2 rounded-full shadow-[0px_4px_45px_0px_rgba(0,0,0,0.05)] border border-gray-100">
                 <button className="p-2 text-gray-400 hover:text-indigo-600 transition-colors lg:hidden" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
                    <Menu size={20} />
                 </button>
                 <button onClick={() => setIsProfileModalOpen(true)} className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold shadow-lg shadow-indigo-100 hover:scale-105 transition-transform overflow-hidden border-2 border-white">
                    {currentUser?.avatar ? <img src={currentUser.avatar} alt="" className="w-full h-full object-cover" /> : currentUser?.name.charAt(0)}
                 </button>
              </div>
            </header>

            <div className="animate-fade-in max-w-[1600px] mx-auto px-1">
              {currentView === 'analytics' ? (
                <div className="space-y-8 pb-20">
                  <div className="bg-white p-2 rounded-2xl shadow-sm inline-flex gap-2 border border-slate-100 mb-4">
                    <button 
                      onClick={() => setAnalyticsViewMode('composition')}
                      className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${analyticsViewMode === 'composition' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      Estate Composition
                    </button>
                    <button 
                      onClick={() => setAnalyticsViewMode('distribution')}
                      className={`px-6 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${analyticsViewMode === 'distribution' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-400 hover:text-slate-600'}`}
                    >
                      Beneficiary Distribution
                    </button>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="lg:col-span-2 bg-white rounded-[40px] p-8 border border-slate-100 shadow-sm relative overflow-hidden">
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-10 relative z-10">
                         <div>
                            <h3 className="text-xl font-black text-slate-900 tracking-tight">
                              {analyticsViewMode === 'composition' ? 'Asset Allocation Strategy' : 'Heritage Succession Map'}
                            </h3>
                            <p className="text-slate-400 text-sm font-medium mt-1">Interactive overview of your total digital estate.</p>
                         </div>
                         
                         <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 self-end sm:self-auto">
                            <button 
                              onClick={() => setChartType('pie')}
                              className={`p-2 rounded-lg transition-all ${chartType === 'pie' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-indigo-400'}`}
                              title="Pie Chart"
                            >
                              <PieIcon size={18} />
                            </button>
                            <button 
                              onClick={() => setChartType('bar')}
                              className={`p-2 rounded-lg transition-all ${chartType === 'bar' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-indigo-400'}`}
                              title="Bar Chart"
                            >
                              <BarChart3 size={18} />
                            </button>
                            <button 
                              onClick={() => setChartType('list')}
                              className={`p-2 rounded-lg transition-all ${chartType === 'list' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-400 hover:text-indigo-400'}`}
                              title="Detailed List"
                            >
                              <List size={18} />
                            </button>
                         </div>
                      </div>

                      <div className="h-[450px] w-full relative z-10">
                        {chartType === 'list' ? (
                          <div className="h-full overflow-y-auto custom-scrollbar space-y-4 pr-2">
                             {(analyticsViewMode === 'composition' ? analyticsData.categoryData : analyticsData.nomineeData).map((item, idx) => (
                               <div key={idx} className="flex items-center justify-between p-5 bg-slate-50 rounded-3xl border border-transparent hover:border-indigo-100 transition-all group">
                                  <div className="flex items-center gap-4">
                                     <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm text-indigo-600 group-hover:scale-110 transition-transform">
                                        {analyticsViewMode === 'composition' ? <Wallet size={20} /> : <Users size={20} />}
                                     </div>
                                     <div>
                                        <div className="text-sm font-black text-slate-900">{item.name}</div>
                                        <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                                           {((item.value / totalEstateValue) * 100).toFixed(1)}% of total estate
                                        </div>
                                     </div>
                                  </div>
                                  <div className="text-right">
                                     <div className="text-sm font-black text-indigo-600">₹{item.value.toLocaleString()}</div>
                                     <div className="flex items-center gap-1 justify-end text-[10px] font-bold text-emerald-500">
                                        <TrendingUp size={10} /> Active
                                     </div>
                                  </div>
                               </div>
                             ))}
                          </div>
                        ) : chartType === 'pie' ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={analyticsViewMode === 'composition' ? analyticsData.categoryData : analyticsData.nomineeData}
                                cx="50%"
                                cy="50%"
                                innerRadius={100}
                                outerRadius={160}
                                paddingAngle={8}
                                dataKey="value"
                                animationBegin={0}
                                animationDuration={1000}
                              >
                                {(analyticsViewMode === 'composition' ? analyticsData.categoryData : analyticsData.nomineeData).map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} stroke="none" />
                                ))}
                              </Pie>
                              <Tooltip 
                                contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', fontWeight: 'bold' }}
                                formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Value']}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                             <BarChart 
                                data={analyticsViewMode === 'composition' ? analyticsData.categoryData : analyticsData.nomineeData} 
                                margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                             >
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94A3B8' }} />
                                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94A3B8' }} />
                                <Tooltip 
                                  cursor={{ fill: '#F8FAFC' }}
                                  contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', fontWeight: 'bold' }}
                                  formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Value']}
                                />
                                <Bar dataKey="value" radius={[15, 15, 0, 0]} barSize={50}>
                                  {(analyticsViewMode === 'composition' ? analyticsData.categoryData : analyticsData.nomineeData).map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Bar>
                             </BarChart>
                          </ResponsiveContainer>
                        )}
                        
                        {chartType === 'pie' && (
                          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                             <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Total Estate</p>
                             <p className="text-2xl font-black text-slate-900 tracking-tighter">₹{(totalEstateValue/10000000).toFixed(2)}Cr</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-6">
                       <div className="bg-[#2B3674] rounded-[40px] p-8 text-white relative overflow-hidden shadow-xl">
                          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
                          <h4 className="text-sm font-bold uppercase tracking-[0.2em] opacity-60 mb-8 flex items-center gap-2">
                             <Target size={18} className="text-indigo-400" /> Goal Readiness
                          </h4>
                          <div className="space-y-6">
                             <div>
                                <div className="flex justify-between items-end mb-2">
                                   <span className="text-xs font-bold text-indigo-100">Succession Health</span>
                                   <span className="text-xl font-black">{estateHealth}%</span>
                                </div>
                                <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                                   <div className="h-full bg-indigo-400 rounded-full transition-all duration-1000" style={{ width: `${estateHealth}%` }}></div>
                                </div>
                             </div>
                             
                             <div className="grid grid-cols-2 gap-4">
                                <div className="bg-white/5 p-4 rounded-3xl border border-white/10">
                                   <div className="text-[10px] font-bold opacity-40 uppercase mb-1">Unmapped</div>
                                   <div className="text-lg font-black">₹{((totalEstateValue * (100 - estateHealth)) / 100).toLocaleString()}</div>
                                </div>
                                <div className="bg-white/5 p-4 rounded-3xl border border-white/10">
                                   <div className="text-[10px] font-bold opacity-40 uppercase mb-1">Categories</div>
                                   <div className="text-lg font-black">{analyticsData.categoryData.length}</div>
                                </div>
                             </div>
                          </div>
                       </div>

                       <div className="bg-white rounded-[40px] p-8 border border-slate-100 shadow-sm h-full">
                          <h4 className="text-sm font-black text-slate-800 tracking-tight mb-8">Institutional exposure</h4>
                          <div className="space-y-6">
                             {analyticsData.institutionData.slice(0, 5).map((inst, i) => (
                               <div key={i} className="flex flex-col gap-2">
                                  <div className="flex justify-between items-center text-[10px] font-bold">
                                     <span className="text-slate-500 uppercase tracking-widest">{inst.name}</span>
                                     <span className="text-slate-900">₹{inst.value.toLocaleString()}</span>
                                  </div>
                                  <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden">
                                     <div 
                                        className="h-full bg-indigo-600 rounded-full transition-all duration-1000" 
                                        style={{ width: `${(inst.value / totalEstateValue) * 100}%` }}
                                      ></div>
                                  </div>
                               </div>
                             ))}
                             {analyticsData.institutionData.length === 0 && (
                               <div className="py-10 text-center text-slate-300 italic text-sm">No institutions registered.</div>
                             )}
                          </div>
                       </div>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-[40px] p-10 border border-slate-100 shadow-sm">
                     <div className="flex items-center gap-4 mb-10">
                        <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600 shadow-sm"><Landmark size={24} /></div>
                        <div>
                           <h3 className="text-xl font-black text-slate-900 tracking-tight">Portfolio Concentration Risk</h3>
                           <p className="text-slate-400 text-sm font-medium">Monitoring your wealth across {analyticsData.institutionData.length} financial partners.</p>
                        </div>
                     </div>
                     
                     <div className="h-[250px] w-full">
                        {assets.length > 0 ? (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={analyticsData.institutionData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                               <defs>
                                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                                     <stop offset="5%" stopColor="#4318FF" stopOpacity={0.2}/>
                                     <stop offset="95%" stopColor="#4318FF" stopOpacity={0}/>
                                  </linearGradient>
                               </defs>
                               <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                               {/* Fix: use 'fontWeight' instead of invalid 'fontBold' property */}
                               <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fontWeight: 'bold', fill: '#94A3B8' }} />
                               <Tooltip 
                                  contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 10px 40px rgba(0,0,0,0.1)', fontWeight: 'bold' }}
                                  formatter={(value: number) => [`₹${value.toLocaleString()}`, 'Risk Exposure']}
                               />
                               <Area type="monotone" dataKey="value" stroke="#4318FF" strokeWidth={4} fillOpacity={1} fill="url(#colorValue)" />
                            </AreaChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="h-full flex items-center justify-center text-slate-300">Insufficient portfolio data.</div>
                        )}
                     </div>
                  </div>
                </div>
              ) : currentView === 'nominee-portal' ? (
                 <NomineePortal user={currentUser!} inheritances={inheritances} />
              ) : currentView === 'beneficiaries' ? (
                <div className="space-y-8 pb-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                      { label: 'Registered Nominees', value: people.length.toString(), icon: Users, color: 'text-indigo-600', bg: 'bg-indigo-50', trend: '+1 this month' },
                      { label: 'Minor Guardians', value: people.filter(p => p.isMinor).length.toString(), icon: Baby, color: 'text-amber-500', bg: 'bg-amber-50', trend: 'Compliance required' },
                      { label: 'Average Estate Share', value: people.length > 0 ? `${(globalStats.reduce((s, p) => s + p.sharePercentage, 0) / people.length).toFixed(1)}%` : '0%', icon: TrendingUp, color: 'text-emerald-500', bg: 'bg-emerald-50', trend: 'Balanced' },
                      { label: 'Vault Verification', value: 'Verified', icon: UserCheck, color: 'text-blue-500', bg: 'bg-blue-50', trend: '100% Signal' }
                    ].map((stat, i) => (
                      <div key={i} className="bg-white p-6 rounded-[20px] shadow-[0px_18px_40px_rgba(112,144,176,0.12)] flex items-center gap-4 border border-transparent hover:border-indigo-100 transition-colors">
                        <div className={`w-14 h-14 rounded-full ${stat.bg} ${stat.color} flex items-center justify-center shrink-0`}>
                          <stat.icon size={24} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] font-bold text-[#707EAE] uppercase tracking-wider mb-1 truncate">{stat.label}</p>
                          <div className="flex items-baseline gap-2">
                            <p className="text-2xl font-black text-[#2B3674]">{stat.value}</p>
                            <span className="text-[9px] font-bold text-gray-400 whitespace-nowrap">{stat.trend}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col xl:flex-row gap-8 items-start">
                    <div className="flex-1 space-y-6 w-full min-0">
                      <div className="bg-white p-8 rounded-[20px] shadow-[0px_18px_40px_rgba(112,144,176,0.12)] flex flex-col md:flex-row justify-between items-center gap-6">
                        <div className="w-full">
                          <h3 className="text-xl font-bold text-[#2B3674]">Heritage Successors</h3>
                          <p className="text-gray-400 text-sm font-medium mt-1">Manage and assign beneficiaries across your verified financial asset vault.</p>
                          
                          <div className="mt-6 flex wrap items-center gap-4">
                            <div className="flex-1 min-w-[200px] flex items-center bg-[#F4F7FE] rounded-2xl px-5 py-3 text-gray-400 focus-within:text-indigo-600 transition-colors border border-transparent focus-within:border-indigo-100">
                              <Search size={18} />
                              <input 
                                type="text" 
                                placeholder="Find a successor by name, relationship, or email..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="bg-transparent border-none outline-none text-sm font-bold ml-3 w-full placeholder:text-gray-400 text-[#2B3674]" 
                              />
                            </div>
                            <button onClick={() => { setEditingNominee(null); setIsAddNomineeModalOpen(true); }} className="flex items-center gap-3 px-8 py-3.5 bg-indigo-600 text-white rounded-2xl font-bold text-sm shadow-xl shadow-indigo-100 hover:bg-indigo-700 hover:-translate-y-0.5 transition-all active:scale-95 shrink-0">
                              <Plus size={20} strokeWidth={3} /> Add New Nominee
                            </button>
                          </div>
                        </div>
                      </div>

                      <div className={`grid gap-6 ${
                        filteredNominees.length === 1 ? 'grid-cols-1' : 
                        filteredNominees.length === 2 ? 'grid-cols-1 lg:grid-cols-2' : 
                        'grid-cols-1 lg:grid-cols-2 xl:grid-cols-3'
                      }`}>
                        {filteredNominees.map(p => {
                          const stat = globalStats.find(gs => gs.id === p.id);
                          return (
                            <NomineeCard 
                              key={p.id} 
                              person={p} 
                              effectiveShare={stat?.sharePercentage || 0} 
                              totalValue={stat?.value || 0} 
                              onEdit={(person) => { setEditingNominee(person); setIsAddNomineeModalOpen(true); }}
                              onDelete={handleDeleteNominee} 
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-8 pb-10">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {[
                      { label: 'Total Estate Value', value: `₹${totalEstateValue.toLocaleString()}`, icon: CreditCard, color: 'text-indigo-600', bg: 'bg-indigo-50' },
                      { label: 'Asset Count', value: assets.length.toString(), icon: Database, color: 'text-[#2B3674]', bg: 'bg-blue-50' },
                      { label: 'Succession Health', value: `${estateHealth}%`, icon: ShieldCheck, color: 'text-emerald-500', bg: 'bg-emerald-50' }
                    ].map((stat, i) => (
                      <div key={i} className="bg-white p-6 rounded-[20px] shadow-[0px_18px_40px_rgba(112,144,176,0.12)] flex items-center gap-4 group hover:-translate-y-1 transition-all duration-300 overflow-hidden border border-transparent hover:border-indigo-100">
                         <div className={`w-14 h-14 rounded-full ${stat.bg} ${stat.color} flex items-center justify-center shrink-0`}>
                            <stat.icon size={24} />
                         </div>
                         <div className="min-w-0">
                            <p className="text-[10px] font-bold text-[#707EAE] uppercase tracking-wider mb-1 truncate">{stat.label}</p>
                            <p className={`font-black text-[#2B3674] truncate ${stat.value.length > 12 ? 'text-lg' : 'text-xl md:text-2xl'}`}>
                              {stat.value}
                            </p>
                         </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-white rounded-[20px] shadow-[0px_18px_40px_rgba(112,144,176,0.12)] overflow-hidden">
                    <div className="p-8 border-b border-gray-100 flex flex-col md:flex-row justify-between items-center gap-4">
                      <div>
                        <h3 className="text-xl font-bold text-[#2B3674]">Financial Asset Vault</h3>
                        <p className="text-gray-400 text-sm font-medium mt-1">Verified records synchronized with national institution registries.</p>
                      </div>
                      <button onClick={() => { setEditingAsset(null); setIsAddAssetModalOpen(true); }} className="flex items-center gap-2 px-8 py-3.5 bg-[#F4F7FE] text-indigo-600 rounded-2xl font-bold text-sm hover:bg-indigo-600 hover:text-white transition-all shadow-sm group">
                        <Plus size={20} strokeWidth={3} className="group-hover:rotate-90 transition-transform" /> New Secured Asset
                      </button>
                    </div>
                    
                    <div className="p-4 space-y-4">
                      {filteredAssets.map(asset => (
                        <AssetCard 
                          key={asset.id} 
                          asset={asset} 
                          people={people} 
                          onUpdate={handleUpdateAssetAllocation} 
                          onDelete={handleDeleteAsset} 
                          onEdit={(a) => { setEditingAsset(a); setIsAddAssetModalOpen(true); }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <footer className="mt-10 py-10 border-t border-gray-100 px-4 flex flex-col md:flex-row justify-between items-center gap-6 max-w-[1600px] mx-auto">
              <p className="text-[10px] font-bold text-[#707EAE] uppercase tracking-[0.25em]">
                © 2026 NomineeDesk • ISO 27001 Certified
              </p>
              <div className="flex wrap items-center justify-center gap-x-10 gap-y-4">
                {['features', 'security', 'compliance', 'privacy', 'terms', 'faq', 'contact'].map((item) => (
                  <button 
                    key={item} 
                    onClick={() => setActiveInfoModal(item as any)}
                    className="text-[10px] font-bold text-[#707EAE] uppercase tracking-[0.2em] hover:text-indigo-600 transition-colors"
                  >
                    {item}
                  </button>
                ))}
              </div>
            </footer>
          </main>

          {currentUser && (
            <UserProfileModal isOpen={isProfileModalOpen} onClose={() => setIsProfileModalOpen(false)} user={currentUser} onSave={handleProfileSave} />
          )}
          <AddNomineeModal 
            isOpen={isAddNomineeModalOpen} 
            onClose={() => { setIsAddNomineeModalOpen(false); setEditingNominee(null); }} 
            onAdd={handleSaveNominee} 
            initialData={editingNominee}
          />
          <AddAssetModal 
            isOpen={isAddAssetModalOpen} 
            onClose={() => { setIsAddAssetModalOpen(false); setEditingAsset(null); }} 
            onAdd={handleSaveAsset} 
            initialData={editingAsset}
          />
        </div>
      )}
      {activeInfoModal && <InfoPage type={activeInfoModal} onClose={() => setActiveInfoModal(null)} />}
    </>
  );
}; export default App;
